import React, { useState, useRef, useEffect } from 'react';
import { BoxWrapper, Prompt } from 'components/Base';
import { ChromePicker, ColorResult } from 'react-color';
import { makeStyles } from '@material-ui/core';
import { useDebouncedCallback } from 'use-debounce';
import api from 'api';
import { Notify } from 'utils';
import { linkColor } from 'utils/colors';
import { useThemeContext } from 'providers/ThemeProvider/ThemeProvider';
import { usePopper } from 'react-popper';

const useStyles = makeStyles({
  swatch: {
    width: '40px',
    height: '40px',
    cursor: 'pointer',
    borderRadius: '15px'
  },
  link: {
    cursor: 'pointer',
    textDecoration: 'none',
    color: linkColor,
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline'
    }
  }
});

interface Color {
  index: number;
  hex_code: string;
}

interface PaletteProps {
  title: string;
  subTitle?: string;
  fieldName: string;
  defaultColors: string[];
  maxColours?: number;
  isFirmAdmin: boolean;
  tooltip?: string;
}

const Palette = ({
  title,
  subTitle,
  fieldName,
  defaultColors,
  maxColours,
  isFirmAdmin,
  tooltip
}: PaletteProps) => {
  const { updateColors } = useThemeContext();
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [isPopperVisible, setIsPopperVisible] = useState(false);
  const classes = useStyles();
  const [colors, setColors] = useState<string[]>(
    maxColours ? defaultColors.slice(0, maxColours) : defaultColors
  );
  const [selectedSwatch, setSelectedSwatch] = useState<number | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isResetPromptOpen, setIsResetPromptOpen] = useState(false);

  const pickerRef = useRef<HTMLDivElement>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'auto'
  });

  const savePalette = useDebouncedCallback((color: string, index: number) => {
    api.savePalette(fieldName, [{ color, index }]).then(() => {
      Notify.success('Colour changed successfully');
      updateColors();
    });
  }, 1000);

  const handleSwatchClick = (index: number, swatchRef: HTMLDivElement) => {
    setSelectedSwatch(index);
    setReferenceElement(swatchRef);
    setIsPopperVisible(true);
  };

  const handleChromePickerChange = (pickedColor: ColorResult) => {
    if (selectedSwatch !== null) {
      const newColors = [...colors];
      newColors[selectedSwatch] = pickedColor.hex;
      setColors(newColors);
      savePalette(pickedColor.hex, selectedSwatch);
    }
  };

  useEffect(() => {
    setColors(maxColours ? defaultColors.slice(0, maxColours) : defaultColors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultColors]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popperElement &&
        !popperElement.contains(event.target as Node) &&
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsPopperVisible(false);
        setSelectedSwatch(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popperElement]);

  const handleToggleShowMore = () => {
    setShowMore(!showMore); // Toggle the state
  };

  const resetPalette = () => {
    setIsResetting(true);
    api
      .resetPalette(fieldName)
      .then(() => {
        updateColors();
      })
      .finally(() => {
        setIsResetPromptOpen(false);
        setIsResetting(false);
      });
  };

  // Limit the swatches to the first 5 when showMore is false
  const visibleColors = showMore ? colors : colors.slice(0, 5);

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          height: '100%'
        }}
      >
        <BoxWrapper
          noBorder
          title={title}
          subTitle={subTitle}
          tooltip={tooltip}
          headerChildren={
            isFirmAdmin &&
            !!colors.length && (
              <div
                className={classes.link}
                onClick={() => {
                  setIsResetPromptOpen(true);
                }}
              >
                Reset Palette
              </div>
            )
          }
          style={{ boxShadow: 'none', height: '100%' }}
        >
          <div style={{ margin: '8px', padding: '8px', maxWidth: '500px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
              {visibleColors.map((color, index) => (
                <div
                  key={index}
                  onClick={e => handleSwatchClick(index, e.currentTarget)}
                  className={classes.swatch}
                  style={{
                    backgroundColor: color,
                    border:
                      selectedSwatch === index ? '3px solid black' : 'none',
                    cursor: 'pointer'
                  }}
                />
              ))}
            </div>

            {colors.length > 5 && (
              <div
                onClick={handleToggleShowMore}
                className={classes.link}
                style={{ marginTop: '10px' }}
              >
                {showMore
                  ? `Show ${colors.length - 5} Less`
                  : `Show ${colors.length - 5} More`}
              </div>
            )}

            {isPopperVisible && selectedSwatch !== null && (
              <div
                ref={setPopperElement}
                style={{ ...styles.popper, zIndex: 2 }}
                {...attributes.popper}
              >
                {isFirmAdmin && (
                  <div ref={pickerRef} style={{ borderRadius: '12px' }}>
                    <ChromePicker
                      color={colors[selectedSwatch]}
                      onChange={handleChromePickerChange}
                      disableAlpha={true}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </BoxWrapper>
      </div>
      <Prompt
        isOpen={isResetPromptOpen}
        title={`Are you sure you want to reset this palette?`}
        confirmAction={() => resetPalette()}
        isProcessing={isResetting}
        cancelAction={() => setIsResetPromptOpen(false)}
      />
    </>
  );
};

export default Palette;
